import React, { useState, useEffect, useRef } from 'react';
import { SystemVariable } from '../types';
import { systemVariablesApi } from '../services/api';
import { Variable, ChevronDown, Search, Check } from 'lucide-react';

interface SystemVariableBlockProps {
  selectedVariableId?: string;
  onChange: (variableId: string) => void;
  disabled?: boolean;
}

export default function SystemVariableBlock({ 
  selectedVariableId, 
  onChange, 
  disabled = false 
}: SystemVariableBlockProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [variables, setVariables] = useState<SystemVariable[]>([]);
  const [categories, setCategories] = useState<{ category: string; variables: SystemVariable[] }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadVariables();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const loadVariables = async () => {
    try {
      const { categories: cats } = await systemVariablesApi.getGrouped();
      setCategories(cats);
      
      // Flache Liste
      const allVars = cats.flatMap((c: any) => c.variables);
      setVariables(allVars);
    } catch (err) {
      console.error('Failed to load variables:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const selectedVariable = variables.find(v => v.id === selectedVariableId);

  const filteredCategories = searchQuery
    ? categories.map(cat => ({
        ...cat,
        variables: cat.variables.filter((v: any) =>
          v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          v.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          v.id.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(cat => cat.variables.length > 0)
    : categories;

  const handleSelect = (variableId: string) => {
    onChange(variableId);
    setIsOpen(false);
    setSearchQuery('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg text-gray-500">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
        <span>Variablen werden geladen...</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between p-3 border rounded-lg text-left transition-colors ${
          disabled
            ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
            : isOpen
            ? 'border-primary-500 ring-2 ring-primary-200'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <div className="flex items-center gap-2">
          <Variable className={`h-5 w-5 ${selectedVariable ? 'text-primary-500' : 'text-gray-400'}`} />
          {selectedVariable ? (
            <div>
              <div className="font-medium text-gray-900">{selectedVariable.name}</div>
              <div className="text-xs text-gray-500">
                Beispiel: {selectedVariable.example}
              </div>
            </div>
          ) : (
            <span className="text-gray-500">Variable auswählen...</span>
          )}
        </div>
        <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder="Variable suchen..."
              />
            </div>
          </div>

          {/* Results */}
          <div className="max-h-72 overflow-y-auto">
            {filteredCategories.length > 0 ? (
              filteredCategories.map((cat) => (
                <div key={cat.category}>
                  <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase sticky top-0">
                    {cat.category}
                  </div>
                  {cat.variables.map((v: any) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => handleSelect(v.id)}
                      className={`w-full px-3 py-2 flex items-center justify-between text-left hover:bg-gray-50 ${
                        v.id === selectedVariableId ? 'bg-primary-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <Variable className={`h-4 w-4 mt-0.5 ${
                          v.id === selectedVariableId ? 'text-primary-500' : 'text-gray-400'
                        }`} />
                        <div>
                          <div className={`text-sm ${
                            v.id === selectedVariableId ? 'font-medium text-primary-700' : 'text-gray-900'
                          }`}>
                            {v.name}
                          </div>
                          <div className="text-xs text-gray-500">{v.example}</div>
                        </div>
                      </div>
                      {v.id === selectedVariableId && (
                        <Check className="h-4 w-4 text-primary-500" />
                      )}
                    </button>
                  ))}
                </div>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-gray-500">
                Keine Variablen gefunden
              </div>
            )}
          </div>

          {/* Info */}
          <div className="p-2 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              System-Variablen werden automatisch mit Vertragsdaten befüllt
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// Draggable Block für den Editor
interface DraggableSystemVariableBlockProps {
  categoryName: string;
}

export function DraggableSystemVariableBlock({ categoryName }: DraggableSystemVariableBlockProps) {
  return (
    <div className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg cursor-grab active:cursor-grabbing">
      <div className="flex items-center gap-2">
        <div className="p-1.5 bg-purple-100 rounded">
          <Variable className="h-4 w-4 text-purple-600" />
        </div>
        <div>
          <div className="font-medium text-purple-900">Vertragsvariable</div>
          <div className="text-xs text-purple-600">Automatisch befüllt</div>
        </div>
      </div>
    </div>
  );
}
